import { useMemo } from "react";
import { WaveBackground } from "@/components/main-page/WaveBackground";

// Seeded PRNG for deterministic confetti
function mulberry32(seed: number) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Particle {
  x: number;
  y: number;
  size: number;
  rotation: number;
  color: string;
  opacity: number;
  shape: "rect" | "circle" | "line";
}

function generateParticles(count: number): Particle[] {
  const rng = mulberry32(42);
  const colors = [
    "#6366f1", // indigo
    "#818cf8", // light indigo
    "#a5b4fc", // lighter indigo
    "#4f46e5", // deeper indigo
    "#c4b5fd", // violet
    "#fbbf24", // amber accent
    "#34d399", // emerald accent
  ];
  const shapes: Particle["shape"][] = ["rect", "circle", "line"];

  return Array.from({ length: count }, () => ({
    x: rng() * 1200,
    y: rng() * 628,
    size: 3 + rng() * 8,
    rotation: rng() * 360,
    color: colors[Math.floor(rng() * colors.length)]!,
    opacity: 0.15 + rng() * 0.35,
    shape: shapes[Math.floor(rng() * shapes.length)]!,
  }));
}

export function LinkedInPreSeedPage() {
  const particles = useMemo(() => generateParticles(60), []);

  return (
    <div
      data-ready="true"
      style={{
        width: 1200,
        height: 628,
        background: "#0a0a0a",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Wave background */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.5 }}>
        <WaveBackground scrollProgress={0} />
      </div>

      {/* Confetti particles */}
      <svg
        style={{ position: "absolute", inset: 0 }}
        width="1200"
        height="628"
        viewBox="0 0 1200 628"
      >
        {particles.map((p, i) => {
          if (p.shape === "circle") {
            return (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={p.size / 2}
                fill={p.color}
                opacity={p.opacity}
              />
            );
          }
          if (p.shape === "line") {
            const len = p.size * 2;
            const rad = (p.rotation * Math.PI) / 180;
            return (
              <line
                key={i}
                x1={p.x}
                y1={p.y}
                x2={p.x + Math.cos(rad) * len}
                y2={p.y + Math.sin(rad) * len}
                stroke={p.color}
                strokeWidth={1.5}
                opacity={p.opacity}
                strokeLinecap="round"
              />
            );
          }
          return (
            <rect
              key={i}
              x={p.x}
              y={p.y}
              width={p.size}
              height={p.size * 0.6}
              rx={1}
              fill={p.color}
              opacity={p.opacity}
              transform={`rotate(${p.rotation} ${p.x + p.size / 2} ${p.y + p.size * 0.3})`}
            />
          );
        })}
      </svg>

      {/* Content */}
      <div style={{ position: "relative", zIndex: 10, textAlign: "center" }}>
        <img
          src="/logo-og.svg"
          alt="COSX"
          style={{ height: 40, marginBottom: 32, display: "block", marginLeft: "auto", marginRight: "auto" }}
        />

        <div
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: 52,
            fontWeight: 300,
            letterSpacing: "-0.02em",
            color: "rgba(255,255,255,0.95)",
            lineHeight: 1.2,
            marginBottom: 16,
          }}
        >
          Pre-Seed Round Closed
        </div>

        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 16,
            fontWeight: 400,
            letterSpacing: "0.15em",
            textTransform: "uppercase" as const,
            color: "rgba(255,255,255,0.4)",
          }}
        >
          Oversubscribed
        </div>
      </div>
    </div>
  );
}
